import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { HandCoins, Wallet, Scale, ChevronDown, ChevronUp, Info } from 'lucide-react';
import StatCard, { StatCardsRow } from '../../components/balances/StatCard';
import {
  DEFAULT_PAGE_SIZE,
  EmptyState,
  ErrorState,
  LoadingHint,
  LoadingState,
  Money,
  Muted,
  PAGE_SIZE_OPTIONS,
  PageHeader,
  PageSubtitle,
  PageTitle,
  Pager,
  Panel,
  PanelHeader,
  PanelTitle,
  PortalPage,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
} from '../../components/agent/AgentPortalUI';
import { agentPortalApi, AgentCommissionClientRow, AgentCommissions } from '../../services/agentPortalService';
import { formatCurrency, formatDate } from '../../services/agentBalancesService';
import { extractErrorMessage } from '../../utils/errorHandler';

const InfoNote = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  padding: 10px 14px;
  border-radius: var(--border-radius-md);
  background: var(--primary-light);
  color: var(--text-primary);
  font-size: 14px;
`;

const PaymentsToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: var(--surface-color);
  color: var(--text-primary);
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--hover-background);
  }

  &:disabled {
    cursor: default;
    color: var(--text-tertiary);
  }
`;

const PaymentsCell = styled.td`
  padding: 0 12px 12px;
  background: var(--background-color);
  border-bottom: 1px solid var(--border-color-light);
`;

const PaymentsTable = styled.table`
  width: 100%;
  max-width: 520px;
  border-collapse: collapse;
  font-size: 13px;
  margin-top: 4px;

  th,
  td {
    padding: 6px 10px;
    text-align: right;
    border-bottom: 1px solid var(--border-color);
  }

  th {
    color: var(--text-secondary);
    font-weight: 600;
  }
`;

const Footnote = styled.p`
  margin: 12px 0 0;
  font-size: 13px;
  color: var(--text-secondary);
`;

const parsePositiveInt = (value: string | null): number | undefined => {
  const parsed = value ? parseInt(value, 10) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const COLUMN_COUNT = 7;

const AgentCommissionsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parsePositiveInt(searchParams.get('page')) ?? 1;
  const requestedPageSize = parsePositiveInt(searchParams.get('pageSize'));
  const pageSize =
    requestedPageSize && PAGE_SIZE_OPTIONS.includes(requestedPageSize) ? requestedPageSize : DEFAULT_PAGE_SIZE;

  const [data, setData] = useState<AgentCommissions | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [expandedClientId, setExpandedClientId] = useState<number | null>(null);

  const updateParams = useCallback(
    (updates: Record<string, number | undefined>) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === undefined) next.delete(key);
          else next.set(key, String(value));
        });
        return next;
      });
    },
    [setSearchParams]
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    agentPortalApi
      .getCommissions(page, pageSize, controller.signal)
      .then(setData)
      .catch(err => {
        if (axios.isCancel(err)) return;
        setError(extractErrorMessage(err, 'שגיאה בטעינת העמלות'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [page, pageSize, reloadKey]);

  const toggleExpanded = (clientId: number) =>
    setExpandedClientId(current => (current === clientId ? null : clientId));

  const renderRow = (row: AgentCommissionClientRow) => {
    const isExpanded = expandedClientId === row.clientId;
    const hasPayments = row.payments.length > 0;

    return (
      <React.Fragment key={row.clientId}>
        <Tr>
          <Td>
            <strong>{row.clientName}</strong>
          </Td>
          <Td>
            <Money>{formatCurrency(row.signingOwed)}</Money>
          </Td>
          <Td>
            {row.approvalOwed != null ? (
              <Money>{formatCurrency(row.approvalOwed)}</Money>
            ) : (
              <Muted title="עמלת האישור מחושבת לאחר אישור הקצבה והפקת החיוב">טרם חושבה</Muted>
            )}
          </Td>
          <Td>
            <Money>{formatCurrency(row.totalOwed)}</Money>
          </Td>
          <Td>
            <Money $tone="positive">{formatCurrency(row.totalPaid)}</Money>
          </Td>
          <Td>
            <strong>
              <Money>{formatCurrency(row.remaining)}</Money>
            </strong>
          </Td>
          <Td>
            <PaymentsToggle
              type="button"
              disabled={!hasPayments}
              aria-expanded={isExpanded}
              onClick={() => toggleExpanded(row.clientId)}
            >
              {hasPayments ? `${row.payments.length} תשלומים` : 'אין תשלומים'}
              {hasPayments && (isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
            </PaymentsToggle>
          </Td>
        </Tr>
        {isExpanded && hasPayments && (
          <tr>
            <PaymentsCell colSpan={COLUMN_COUNT}>
              <PaymentsTable>
                <thead>
                  <tr>
                    <th>תאריך תשלום</th>
                    <th>סכום</th>
                    <th>אסמכתא</th>
                  </tr>
                </thead>
                <tbody>
                  {row.payments.map((payment, index) => (
                    <tr key={index}>
                      <td>{formatDate(payment.paymentDate)}</td>
                      <td>
                        <Money>{formatCurrency(payment.amount)}</Money>
                      </td>
                      <td>{payment.reference || <Muted>-</Muted>}</td>
                    </tr>
                  ))}
                </tbody>
              </PaymentsTable>
            </PaymentsCell>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const renderTable = () => {
    if (error) return <ErrorState text={error} onRetry={() => setReloadKey(k => k + 1)} />;
    if (!data) return <LoadingState />;
    if (data.items.length === 0) return <EmptyState text="עדיין אין לקוחות שחתמו" />;

    return (
      <>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>לקוח</Th>
                <Th>עמלת חתימה</Th>
                <Th>עמלת אישור</Th>
                <Th>{'סה"כ עמלה'}</Th>
                <Th>שולם</Th>
                <Th>יתרה</Th>
                <Th>תשלומים</Th>
              </tr>
            </thead>
            <tbody>{data.items.map(renderRow)}</tbody>
          </Table>
        </TableWrap>
        <Pager
          page={page}
          pageSize={pageSize}
          totalCount={data.totalCount}
          onPageChange={p => updateParams({ page: p > 1 ? p : undefined })}
          onPageSizeChange={size => updateParams({ page: undefined, pageSize: size === DEFAULT_PAGE_SIZE ? undefined : size })}
        />
      </>
    );
  };

  return (
    <PortalPage>
      <PageHeader>
        <PageTitle>
          <HandCoins size={26} />
          העמלות שלי
        </PageTitle>
        <PageSubtitle>העמלות על הלקוחות שהפנית, מה שכבר שולם ומה שנותר לתשלום</PageSubtitle>
      </PageHeader>

      <StatCardsRow>
        <StatCard
          title="סה&quot;כ עמלות"
          value={formatCurrency(data?.totalOwed ?? 0)}
          color="var(--primary-color)"
          icon={<HandCoins size={20} />}
        />
        <StatCard
          title="שולם"
          value={formatCurrency(data?.totalPaid ?? 0)}
          color="var(--success-color)"
          icon={<Wallet size={20} />}
        />
        <StatCard
          title="יתרה לתשלום"
          value={formatCurrency(data?.remaining ?? 0)}
          color="var(--warning-color)"
          icon={<Scale size={20} />}
        />
      </StatCardsRow>

      {data && data.pendingLeadCount > 0 && (
        <InfoNote>
          <Info size={18} color="var(--primary-color)" />
          {data.pendingLeadCount === 1
            ? 'ליד אחד בטיפול — העמלה עליו תחושב לאחר חתימה'
            : `${data.pendingLeadCount} לידים בטיפול — העמלה עליהם תחושב לאחר חתימה`}
        </InfoNote>
      )}

      <Panel>
        <PanelHeader>
          <PanelTitle>פירוט לפי לקוח</PanelTitle>
          {loading && data && <LoadingHint>מעדכן...</LoadingHint>}
        </PanelHeader>
        {renderTable()}
      </Panel>

      <Footnote>
        עמלת החתימה נצברת עם חתימת הלקוח. עמלת האישור מחושבת לאחר אישור הקצבה והפקת החיוב.
      </Footnote>
    </PortalPage>
  );
};

export default AgentCommissionsPage;
